import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export function AuditIssueFilter() {
  const nav = useNavigate({ from: "/app/internal-audit/issues" });
  const search = useSearch({ from: "/app/internal-audit/issues" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const searchFilter = findCondition(filters, "title");
  const severityFilter = findCondition(filters, "severity");
  const statusFilter = findCondition(filters, "status");
  const departmentFilter = findCondition(filters, "departmentId");

  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((searchFilter?.value as string) || ""),
    [searchFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "title",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "title");
    }
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const severityValue = (severityFilter?.value as string) || "";
  const statusValue = (statusFilter?.value as string) || "";
  const departmentValue = (departmentFilter?.value as string) || "";

  const hasFilter =
    !!searchFilter?.value || !!severityValue || !!statusValue || !!departmentValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="issue-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ (ຊື່ບັນຫາ)..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={severityValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "severity");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "severity",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            options={[
              { value: "all", label: "ທຸກລະດັບ" },
              { value: "low", label: "ເບົາ" },
              { value: "medium", label: "ກາງ" },
              { value: "high", label: "ສູງ" },
              { value: "critical", label: "ວິກິດ" },
            ]}
            placeholder="ລະດັບຄວາມຮຸນແຮງ"
            className="h-8 w-[140px]"
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            options={[
              { value: "all", label: "ທຸກສະຖານະ" },
              { value: "open", label: "ເປີດ" },
              { value: "in_progress", label: "ກຳລັງແກ້ໄຂ" },
              { value: "resolved", label: "ແກ້ໄຂແລ້ວ" },
              { value: "closed", label: "ປິດ" },
            ]}
            placeholder="ສະຖານະ"
            className="h-8 w-[140px]"
          />
        </div>
        {hasFilter && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: undefined,
                },
              });
            }}
          >
            <XIcon className="mr-2 h-4 w-4" />
            ລ້າງການກອງ
          </Button>
        )}
      </div>
    </div>
  );
}

